<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import signupHeroIllustration from "shared-ui/assets/illustrations/signup-hero.avif";
import { useRiskAnalysisStore } from "shared-ui/stores";
import { onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const { t } = useI18n();

useSeoMeta({
  title: () => t("risk_analysis.signup.title"),
});

definePage({
  meta: {
    requiresAuth: false,
    layout: "LoginLayout",
  },
});

const riskAnalysisStore = useRiskAnalysisStore();
const domain = ref<string>("");
const errorMessage = ref<string>("");
const isSubmitted = ref<boolean>(false);
const router = useRouter();

onUnmounted(() => {
  abortAnalysis();
});

function abortAnalysis(): void {
  reset();
  riskAnalysisStore.abortRiskAnalysis();
}

function reset(): void {
  isSubmitted.value = false;
  domain.value = "";
  errorMessage.value = "";
}

async function redirectToSignup(): Promise<void> {
  await router.push({
    path: "/",
    query: {
      msg: t("risk_analysis.signup.failed_analysis"),
    },
  });
}
</script>

<template>
  <!-- Use v-show because DomainForm needs to be mounted at all times -->
  <div v-show="!isSubmitted" class="flex h-full items-center justify-center">
    <div
      class="grid size-full max-w-screen-2xl grid-cols-1 items-center justify-center gap-28 md:grid-cols-2"
    >
      <img
        :src="signupHeroIllustration"
        alt=""
        class="hidden size-full rounded-4xl object-cover md:block"
      />
      <div
        class="flex flex-col items-start justify-start gap-6 md:justify-center"
      >
        <p class="text-lg font-normal text-neutral-600">
          {{ t("risk_analysis.signup.try_for_free") }}
        </p>
        <h1 class="text-5xl! font-normal! text-primary-black!">
          {{ t("risk_analysis.signup.lets_get_started") }}
        </h1>
        <p class="max-w-xl text-xl font-normal text-neutral-600">
          {{ t("risk_analysis.signup.powerful_protection") }}
        </p>
        <h3 class="text-lg! font-medium! text-primary-black!">
          {{ t("risk_analysis.signup.enter_company_domain") }}
        </h3>
        <DomainForm
          v-model:domain="domain"
          v-model:error-message="errorMessage"
          v-model:is-submitted="isSubmitted"
          @analyzing-failed="redirectToSignup"
        />
        <h3 class="text-lg! font-medium! text-primary-black!">
          {{ t("risk_analysis.signup.included_in_trial") }}
        </h3>
        <div
          class="grid w-full grid-cols-1 justify-between gap-3 sm:grid-cols-2"
        >
          <InfoCheck :info="t('risk_analysis.signup.automated_monitoring')" />
          <InfoCheck :info="t('risk_analysis.signup.deep_security_insights')" />
          <InfoCheck :info="t('risk_analysis.signup.one_click_protection')" />
          <InfoCheck :info="t('risk_analysis.signup.risk_level_threats')" />
          <InfoCheck
            :info="t('risk_analysis.signup.automated_device_scanning')"
          />
          <InfoCheck :info="t('risk_analysis.signup.breach_cost_estimates')" />
        </div>
      </div>
    </div>
  </div>
  <RiskAnalysisLoading
    v-if="isSubmitted"
    :domain="domain"
    @stop-analyzing="abortAnalysis"
  />
</template>
